<!-- Main Container: Responsive Flex Layout -->
<div class="responsive-container">

  <!-- Section 1: Detected Devices (Left side on desktop, bottom on mobile) -->
  <section class="detected-devices-section section-left">

    <customer-title-content
      class="mb-3"
      [title]="labels.detectedDevices + ' (' + detectedDevices.length + ')'"
      [content]="mergedLabels.clickToAddToYourPlant">
    </customer-title-content>

    <!-- Detected Devices List -->
    <ng-container *ngIf="detectedDevices.length > 0; else noDetectedDevices">
      <div *ngFor="let device of sortedDetectedDevices" class="mb-3">
        <app-card-accordion
          [ngClass]="{
            'card-accordion-bg-green': device.online && !isDeviceAdded(device.serial) && !hasValidationError(device.serial),
            'card-accordion-bg-grey': !device.online,
            'card-accordion-bg-yellow': !device.online && !isDeviceAdded(device.serial) && !hasValidationError(device.serial),
            'card-accordion-bg-red': hasValidationError(device.serial),
            'opacity-50': isDeviceAdded(device.serial)
          }"
          mode="card">
          <customer-item
            [matTooltip]="mergedLabels.addedDevice"
            [matTooltipDisabled]="!isDeviceAdded(device.serial)"
            [class.item-conflict]="hasValidationError(device.serial)"
            [deviceItemContent]="device.serial + ' (' + device.type + ')'"
            [deviceItemSubContent]="hasValidationError(device.serial) ? getDeviceValidationError(device.serial) : (device.online ? 'Online' : 'Offline')"
            [primaryAction]="!isDeviceAdded(device.serial) && !hasValidationError(device.serial) ? {
              classes: 'text-accent',
              icon: 'add',
              clickFn: getAddDetectedDeviceAction(device)
            } : undefined">
          </customer-item>
        </app-card-accordion>
      </div>
    </ng-container>

    <ng-template #noDetectedDevices>
      <div class="fx-center p-6 text-grey-400">
        <div class="fx-col fx-center fx-gap-2">
          <mat-icon class="text-6xl">search_off</mat-icon>
          <customer-title-content
            [title]="mergedLabels.noDetectedDevices"
            [content]="mergedLabels.detectedDevicesDescription">
          </customer-title-content>
        </div>
      </div>
    </ng-template>

    <customer-title-content
      class="scan-section"
      [title]="mergedLabels.detectedDevices"
      [content]="mergedLabels.detectedDevicesDescription">
    </customer-title-content>

    <!-- Scan Button -->
    <div class="fx-row fx-justify-end fx-gap-2 my-3">
      <!-- Start Scan Button -->
      <customer-simple-button
        [label]="componentState.rescanStatus === AHscanDevicesEnum.idle ? mergedLabels.startRescan : mergedLabels.scanning"
        [disabled]="componentState.rescanStatus !== AHscanDevicesEnum.idle || componentState.loading || aHIsOffline"
        [matTooltip]="aHIsOffline ? mergedLabels.azzurroHubOffline : ''"
        [matTooltipDisabled]="!aHIsOffline"
        [action]="getStartRescanAction()">
      </customer-simple-button>
    </div>

  </section>

  <!-- Separator (only visible in column layout) -->
  <hr class="column-separator border-t-1 border-grey-100 my-6" />

  <!-- Right Side Container: Added Devices and Advanced Section -->
  <div class="section-right space-y-6">

    <!-- Section 2: Added Devices List -->
    <section class="added-devices-section">
      <customer-title-content
        [title]="mergedLabels.devicesAddedToAdvancedPlant + ' (' + addedDevices.length + ')'"
        [content]="mergedLabels.deviceListContent">
      </customer-title-content>

      <!-- Added Devices List -->
      <ng-container *ngIf="addedDevices.length > 0; else noAddedDevices">
        <div class="fx-col fx-gap-3 mt-3">
          <div *ngFor="let device of sortedAddedDevices">
            <app-card-accordion class="card-accordion-bg-green fx-g-1" mode="card">
              <customer-item
                [deviceItemContent]="device.serial"
                [deviceItemSubContent]="mergedLabels.addedDevice"
                [ngClass]="{
                  'item-uninstall': device.dismiss === 'Uninstall',
                  'item-delete': device.dismiss === 'Delete'
                }"
                [primaryAction]="isVerifiedMode ? (device.dismiss === 'Uninstall' ? {
                  classes: 'text-grey-300',
                  icon: 'undo',
                  hoverText: mergedLabels.undo,
                  clickFn: getDismissDeviceAction(device.id)
                } : device.dismiss === 'Delete' ? {
                  classes: 'text-grey-300',
                  icon: 'undo',
                  hoverText: mergedLabels.undo,
                  clickFn: getDismissDeviceAction(device.id)
                } : {
                  classes: 'text-grey-300',
                  icon: 'archive',
                  hoverText: mergedLabels.uninstall,
                  clickFn: getDismissDeviceAction(device.id, 'Uninstall')
                }) : {
                  classes: 'text-red-400',
                  icon: 'delete',
                  clickFn: getRemoveDeviceAction(device.id)
                }"
                [secondaryAction]="isVerifiedMode && !device.dismiss ? {
                  classes: 'text-red-400',
                  icon: 'delete',
                  hoverText: mergedLabels.delete,
                  clickFn: getDismissDeviceAction(device.id, 'Delete')
                } : undefined">
              </customer-item>
            </app-card-accordion>
          </div>
        </div>
      </ng-container>

      <ng-template #noAddedDevices>
        <div class="fx-center p-6 text-grey-400 mt-3">
          <div class="fx-col fx-center fx-gap-2 text-center">
            <mat-icon class="text-6xl">devices</mat-icon>
            <customer-title-content
              [title]="mergedLabels.noAddedDevices"
              [content]="mergedLabels.noAddedDevicesDescription">
            </customer-title-content>
          </div>
        </div>
      </ng-template>
    </section>

    <!-- Section 3: Advanced Section (Accordion) -->
    <section class="advanced-section mt-6">
      <div class="advanced-accordion">
        <!-- Accordion Header -->
        <div class="advanced-header cursor-pointer p-4 bg-grey-50 rounded-lg border border-grey-200 hover:bg-grey-100 transition-colors"
             (click)="toggleAdvancedSection()">
          <div class="fx-row fx-align-center fx-justify-between">
            <div class="fx-col">
              <span class="text-sm font-medium text-grey-700">{{ mergedLabels.advanced }}</span>
              <span class="text-xs text-grey-500 mt-1">{{ mergedLabels.advancedDescription }}</span>
            </div>
            <mat-icon class="text-accent transition-transform"
                      [ngClass]="{ 'rotate-180': isAdvancedExpanded }">
              expand_more
            </mat-icon>
          </div>
        </div>

        <!-- Accordion Content (Manual Device Entry) -->
        <div class="advanced-content" *ngIf="isAdvancedExpanded">
          <div class="p-4 border-l-2 border-grey-200 ml-3 mt-3">
            <customer-title-content
              [title]="mergedLabels.manualDeviceList"
              [content]="mergedLabels.manualDeviceListDescription">
            </customer-title-content>

            <!-- Single Input Field -->
            <div class="fx-row fx-wrap fx-gap-3 pos-sticky inset-b-0 py-1 fx-g-1 mt-3">
              <customer-simple-input
                class="fx-g-1 min-h-15"
                [placeholder]="mergedLabels.manualDeviceListPlaceholder"
                [input]="componentState.inputSerial"
                [errorMessage]="componentState.inputSerial.errors | deviceSerialValidatorPipe : mergedLabels">
              </customer-simple-input>

              <customer-simple-button
                [label]="mergedLabels.addDevice"
                [disabled]="componentState.inputSerial.invalid || componentState.inputSerial.pending"
                [action]="getAddDeviceFromInputAction()">
              </customer-simple-button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>

</div>
